import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Pagination, MenuProps, Dropdown, Space, Spin } from 'antd';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import ProviderStore from 'store/Provider';
import { DownOutlined } from '@ant-design/icons';
import { PatientsFeedback } from 'types/common';
import { getTimeStr } from 'utils/calendar';
import ReplyFeedbackModal from '../ReplyFeedbackModal';
import { getReputationFeedbackList, getReputationFeedbackOverviewData } from 'api/survey';
import { IFeedbackOverviewData } from 'types/reputation';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import { isFreeUser } from 'utils/provider';
import { openSubscriptionModal } from 'utils/globalLayerControl';
import Star from 'components/Star';
import { getFullName } from 'utils/common';
import ReplyUpgradeTipModal from '../ReplyUpgradeTipModal';

enum EFeedbackFilter {
    OLDEST = 1,
    NEWEST = 0,
}

enum EFeedbackType {
    NORMAL = 1,
    ANONYMOUS = 2,
    REPLYED = 3,
}

const Feedback = () => {
    const navigate = useNavigate();
    const [getUser] = ProviderStore.useStore();
    const [showReply, setShowReply] = React.useState<boolean>(false);
    const [currentFeedback, setCurrentFeedback] = React.useState<PatientsFeedback>();
    const [currentFeedbackType, setCurrentFeedbackType] = React.useState<EFeedbackType>(EFeedbackType.NORMAL);
    const [feedbackData, setFeedbackData] = useState<PatientsFeedback[]>([]);
    const [anonymousFeedbackData, setAnonymousFeedbackData] = useState<PatientsFeedback[]>([]);
    const [repliedFeedbackData, setRepliedFeedbackData] = useState<PatientsFeedback[]>([]);
    const [overviewData, setOverviewData] = useState<IFeedbackOverviewData>({
        averageSurveyRating: 0,
        feedbackReceived: 0,
        invitationSend: 0,
        surveyDeliverySend: 0,
        surveySendThisWeek: 0,
        surveySendTotal: 0,
    });
    const [feedbackFilter, setFeedbackFilter] = React.useState<EFeedbackFilter>(EFeedbackFilter.NEWEST);
    const [feedbackListPage, setFeedbackListPage] = useState(1);
    const [feedbackListPageSize, setFeedbackListPageSize] = useState(10);
    const [totalFeedback, setTotalFeedback] = useState(0);

    const [anonymousFeedbackFilter, setAnonymousFeedbackFilter] = React.useState<EFeedbackFilter>(EFeedbackFilter.NEWEST);
    const [anonymousFeedbackListPage, setAnonymousFeedbackListPage] = useState(1);
    const [anonymousFeedbackListPageSize, setAnonymousFeedbackListPageSize] = useState(10);
    const [anonymousTotalFeedback, setAnonymousTotalFeedback] = useState(0);

    const [repliedFeedbackFilter, setRepliedFeedbackFilter] = React.useState<EFeedbackFilter>(EFeedbackFilter.NEWEST);
    const [repliedFeedbackListPage, setRepliedFeedbackListPage] = useState(1);
    const [repliedFeedbackListPageSize, setRepliedFeedbackListPageSize] = useState(10);
    const [repliedTotalFeedback, setRepliedTotalFeedback] = useState(0);
    const [loading, setLoading] = React.useState(false);

    const [getDataUpdatedFlag] = DataUpdatedFlagStore.useStore();
    const patientFeedbackListUpdatedFlag = getDataUpdatedFlag('patientFeedbackListUpdated');

    const isFree = isFreeUser();
    const user = getUser('data');
    const hasLoaded = !!user?.email;

    const showUpgrade = !!isFree && hasLoaded;
    const showChannelStore = !isFree && !user?.gmail && hasLoaded;

    const canReply = !showUpgrade && !showChannelStore;
    console.log('canReply', canReply);
    const [replyUpgradeTipModalOpen, setReplyUpgradeTipModalOpen] = React.useState(false);

    const [replyUpgradeTipModalTitle, setReplyUpgradeTipModalTitle] = React.useState('');
    const [replyUpgradeTipModalDescription, setReplyUpgradeTipModalDescription] = React.useState('');
    const [replyUpgradeTipModalButtonText, setReplyUpgradeTipModalButtonText] = React.useState('');
    const [replyUpgradeTipModalButtonCallback, setReplyUpgradeTipModalButtonCallback] = React.useState<(() => void) | null>(null);
    useEffect(() => {
        if (!canReply) {
            if (isFree) {
                setReplyUpgradeTipModalTitle('Upgrade now to unlock patient responses');
                setReplyUpgradeTipModalDescription('Replies are available with a paid plan. Upgrade to engage with patients, address feedback, and build stronger relationships.');
                setReplyUpgradeTipModalButtonText('Upgrade now');
                setReplyUpgradeTipModalButtonCallback(() => openSubscriptionModal);
            } else {
                setReplyUpgradeTipModalTitle('Now complete Practice Front to unlock patient responses');
                setReplyUpgradeTipModalDescription('');
                setReplyUpgradeTipModalButtonText('Complete now');
                setReplyUpgradeTipModalButtonCallback(() => () => navigate('/dashboard/marketing?mainTab=external&subTab=practice-front'));
            }
        }
    }, [canReply, isFree, navigate]);

    const fetchOverviewData = async () => {
        try {
            const res = await getReputationFeedbackOverviewData();

            if (res && !res.error && res.data?.data) {
                setOverviewData(res.data.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const getFeedbackList = async (
        { type, current, size, order, replied }:
            { type: EFeedbackType, current?: number, size?: number, order: EFeedbackFilter, replied?: boolean },
    ) => {
        try {
            let _size = size || feedbackListPageSize;
            let _current = current || feedbackListPage;
            if (type === EFeedbackType.ANONYMOUS) {
                _size = size || anonymousFeedbackListPageSize;
                _current = current || anonymousFeedbackListPage;
            }
            if (type === EFeedbackType.REPLYED) {
                _size = size || repliedFeedbackListPageSize;
                _current = current || repliedFeedbackListPage;
            }
            setLoading(true);
            const res = await getReputationFeedbackList({
                isAnonymous: type === EFeedbackType.ANONYMOUS,
                orderAsc: order === EFeedbackFilter.OLDEST,
                size: _size,
                current: _current,
                replied: replied || false,
            });
            setLoading(false);

            if (res && !res.error) {
                const { records = [], total = 0 } = res.data?.data || {};
                if (type === EFeedbackType.NORMAL) {
                    setFeedbackData(records);
                    setTotalFeedback(total);
                } else if (replied) {
                    setRepliedFeedbackData(records);
                    setRepliedTotalFeedback(total);
                } else {
                    setAnonymousFeedbackData(records);
                    setAnonymousTotalFeedback(total);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchOverviewData();
        getFeedbackList({ type: EFeedbackType.NORMAL, order: EFeedbackFilter.NEWEST });
        getFeedbackList({ type: EFeedbackType.ANONYMOUS, order: EFeedbackFilter.NEWEST });
        getFeedbackList({ type: EFeedbackType.REPLYED, replied: true, order: EFeedbackFilter.NEWEST });
    }, []);

    // const handleFeedbackTypeChange = (type: EFeedbackType) => {
    //     const data:any = { type, current: 1, replied: type === EFeedbackType.REPLYED };
    //     if (currentFeedbackType === EFeedbackType.NORMAL) {
    //         data.order = feedbackFilter === EFeedbackFilter.OLDEST;
    //     }
    //     if (currentFeedbackType === EFeedbackType.ANONYMOUS) {
    //         data.order = anonymousFeedbackFilter === EFeedbackFilter.OLDEST;
    //     }
    //     if (currentFeedbackType === EFeedbackType.REPLYED) {
    //         data.order = repliedFeedbackFilter === EFeedbackFilter.OLDEST;
    //     }
    //     getFeedbackList(data);
    // };

    const handlePaginationChange = (page: number, pageSize: number) => {
        const data: any = { type: currentFeedbackType, current: page, size: pageSize, replied: currentFeedbackType === EFeedbackType.REPLYED };
        if (currentFeedbackType === EFeedbackType.NORMAL) {
            setFeedbackListPage(page);
            setFeedbackListPageSize(pageSize);
            data.order = feedbackFilter === EFeedbackFilter.OLDEST;
        }
        if (currentFeedbackType === EFeedbackType.ANONYMOUS) {
            setAnonymousFeedbackListPage(page);
            setAnonymousFeedbackListPageSize(pageSize);
            data.order = anonymousFeedbackFilter === EFeedbackFilter.OLDEST;
        }
        if (currentFeedbackType === EFeedbackType.REPLYED) {
            setRepliedFeedbackListPage(page);
            setRepliedFeedbackListPageSize(pageSize);
            data.order = repliedFeedbackFilter === EFeedbackFilter.OLDEST;
        }

        getFeedbackList(data);
    };

    useEffect(() => {
        if (patientFeedbackListUpdatedFlag) {
            fetchOverviewData();
            getFeedbackList({ type: EFeedbackType.NORMAL, order: feedbackFilter });
            getFeedbackList({ type: EFeedbackType.ANONYMOUS, order: anonymousFeedbackFilter });
            getFeedbackList({ type: EFeedbackType.REPLYED, order: repliedFeedbackFilter, replied: true });
        }
    }, [patientFeedbackListUpdatedFlag]);

    const feedbackFilterItems: MenuProps['items'] = [
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setFeedbackFilter(EFeedbackFilter.NEWEST);
                    setFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.NORMAL, order: EFeedbackFilter.NEWEST, current: 1 });
                }}
            >Newest</span>,
            key: EFeedbackFilter.NEWEST,
        },
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setFeedbackFilter(EFeedbackFilter.OLDEST);
                    setFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.NORMAL, order: EFeedbackFilter.OLDEST, current: 1 });
                }}
            >Oldest</span>,
            key: EFeedbackFilter.OLDEST,
        },
    ];

    let feedbackFilterLabel = '';
    if (feedbackFilter === EFeedbackFilter.NEWEST) {
        feedbackFilterLabel = 'Newest';
    }
    if (feedbackFilter === EFeedbackFilter.OLDEST) {
        feedbackFilterLabel = 'Oldest';
    }

    const anonymousFeedbackFilterItems: MenuProps['items'] = [
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setAnonymousFeedbackFilter(EFeedbackFilter.NEWEST);
                    setAnonymousFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.ANONYMOUS, order: EFeedbackFilter.NEWEST, current: 1 });
                }}
            >Newest</span>,
            key: EFeedbackFilter.NEWEST,
        },
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setAnonymousFeedbackFilter(EFeedbackFilter.OLDEST);
                    setAnonymousFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.ANONYMOUS, order: EFeedbackFilter.OLDEST, current: 1 });
                }}
            >Oldest</span>,
            key: EFeedbackFilter.OLDEST,
        },
    ];

    let anonymousFeedbackFilterLabel = '';
    if (anonymousFeedbackFilter === EFeedbackFilter.NEWEST) {
        anonymousFeedbackFilterLabel = 'Newest';
    }
    if (anonymousFeedbackFilter === EFeedbackFilter.OLDEST) {
        anonymousFeedbackFilterLabel = 'Oldest';
    }

    const repliedFeedbackFilterItems: MenuProps['items'] = [
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setRepliedFeedbackFilter(EFeedbackFilter.NEWEST);
                    setRepliedFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.REPLYED, order: EFeedbackFilter.NEWEST, current: 1, replied: true });
                }}
            >Newest</span>,
            key: EFeedbackFilter.NEWEST,
        },
        {
            label: <span
                className={s.dropItem}
                onClick={() => {
                    setRepliedFeedbackFilter(EFeedbackFilter.OLDEST);
                    setRepliedFeedbackListPage(1);
                    getFeedbackList({ type: EFeedbackType.REPLYED, order: EFeedbackFilter.OLDEST, current: 1, replied: true });
                }}
            >Oldest</span>,
            key: EFeedbackFilter.OLDEST,
        },
    ];

    let repliedFeedbackFilterLabel = '';
    if (repliedFeedbackFilter === EFeedbackFilter.NEWEST) {
        repliedFeedbackFilterLabel = 'Newest';
    }
    if (repliedFeedbackFilter === EFeedbackFilter.OLDEST) {
        repliedFeedbackFilterLabel = 'Oldest';
    }

    const {
        averageSurveyRating,
        feedbackReceived,
    } = overviewData;

    return (
        <div className={s.wrap}>
            <ReplyUpgradeTipModal
                title={replyUpgradeTipModalTitle}
                description={replyUpgradeTipModalDescription}
                buttonText={replyUpgradeTipModalButtonText}
                open={replyUpgradeTipModalOpen}
                onCancel={() => setReplyUpgradeTipModalOpen(false)}
                onOk={() => {
                    setReplyUpgradeTipModalOpen(false);
                    replyUpgradeTipModalButtonCallback?.();
                }}
            />
            <div className={s.header}>
                <div className={s.rowWrap}>
                    <div className={s.rowIn}>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Total feedback received</div>
                            </div>
                            <div className={s.bottom}>
                                <span className={s.val}>{feedbackReceived || 0}</span>
                            </div>
                        </div>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Average survey rating</div>
                            </div>
                            <div className={s.bottom}>
                                <span className={s.val}>{averageSurveyRating || 0}</span> <span className={s.val2}> out of 5</span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <div className={s.feedback}>
                <div className={s.feedbackHeader}>
                    <h2>Feedback received</h2>
                </div>
                <div className={s.feedbackType}>
                    <div
                        onClick={() => {
                            // setFeedbackListPage(1);
                            // setAnonymousFeedbackListPage(1);
                            // setRepliedFeedbackListPage(1);
                            // handleFeedbackTypeChange(EFeedbackType.NORMAL);
                            setCurrentFeedbackType(EFeedbackType.NORMAL);
                        }}
                        className={currentFeedbackType === EFeedbackType.NORMAL ? `${s.typeItem} ${s.typeItemActive}` : s.typeItem}
                    >
                        Feedback needs reply ({totalFeedback})
                    </div>
                    <div
                        onClick={() => {
                            // setFeedbackListPage(1);
                            // setAnonymousFeedbackListPage(1);
                            // setRepliedFeedbackListPage(1);
                            // handleFeedbackTypeChange(EFeedbackType.ANONYMOUS);
                            setCurrentFeedbackType(EFeedbackType.ANONYMOUS);
                        }}
                        className={currentFeedbackType === EFeedbackType.ANONYMOUS ? `${s.typeItem} ${s.typeItemActive}` : s.typeItem}
                    >
                        Anonymous feedback ({anonymousTotalFeedback})
                    </div>
                    <div
                        onClick={() => {
                            // setFeedbackListPage(1);
                            // setAnonymousFeedbackListPage(1);
                            // setRepliedFeedbackListPage(1);
                            // handleFeedbackTypeChange(EFeedbackType.REPLYED);
                            setCurrentFeedbackType(EFeedbackType.REPLYED);
                        }}
                        className={currentFeedbackType === EFeedbackType.REPLYED ? `${s.typeItem} ${s.typeItemActive}` : s.typeItem}
                    >
                        Responded feedback ({repliedTotalFeedback})
                    </div>
                </div>
                <div className={s.tips}>
                    {
                        showUpgrade && (
                            <div className={s.upgrade}>
                                <div className={s.nodeLeft}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M5 2C5.55228 2 6 2.44772 6 3V4H7C7.55228 4 8 4.44772 8 5C8 5.55228 7.55228 6 7 6H6V7C6 7.55228 5.55228 8 5 8C4.44772 8 4 7.55228 4 7V6H3C2.44772 6 2 5.55228 2 5C2 4.44772 2.44772 4 3 4H4V3C4 2.44772 4.44772 2 5 2ZM5 12C5.55228 12 6 12.4477 6 13V14H7C7.55228 14 8 14.4477 8 15C8 15.5523 7.55228 16 7 16H6V17C6 17.5523 5.55228 18 5 18C4.44772 18 4 17.5523 4 17V16H3C2.44772 16 2 15.5523 2 15C2 14.4477 2.44772 14 3 14H4V13C4 12.4477 4.44772 12 5 12Z" fill="#FBBF24" />
                                        <path fillRule="evenodd" clipRule="evenodd" d="M11.9999 2C12.4537 2 12.8505 2.30548 12.9667 2.74411L14.1459 7.19893L17.4997 9.13381C17.8092 9.31241 17.9999 9.64262 17.9999 10C17.9999 10.3574 17.8092 10.6876 17.4997 10.8662L14.1459 12.8011L12.9667 17.2559C12.8505 17.6945 12.4537 18 11.9999 18C11.5462 18 11.1493 17.6945 11.0332 17.2559L9.85402 12.8011L6.50027 10.8662C6.19072 10.6876 6 10.3574 6 10C6 9.64262 6.19072 9.31241 6.50027 9.13382L9.85402 7.19893L11.0332 2.74411C11.1493 2.30548 11.5462 2 11.9999 2Z" fill="#FBBF24" />
                                    </svg>
                                </div>
                                <div className={s.nodeRight}>
                                    <div className={s.text}>
                                        Elevate patient communication with our &apos;Reply Feedback&apos; tool. As a premium feature, it enables timely responses to patient feedback, building stronger trust and engagement. Enhance your practice&apos;s responsiveness and patient care by upgrading your plan now.
                                    </div>
                                    <div
                                        onClick={() => {
                                            openSubscriptionModal();
                                        }}
                                        className={s.upText}
                                    >
                                        Upgrade plan
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {
                        showChannelStore && (
                            <div className={s.upgrade}>
                                <div className={s.nodeLeft}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M5 2C5.55228 2 6 2.44772 6 3V4H7C7.55228 4 8 4.44772 8 5C8 5.55228 7.55228 6 7 6H6V7C6 7.55228 5.55228 8 5 8C4.44772 8 4 7.55228 4 7V6H3C2.44772 6 2 5.55228 2 5C2 4.44772 2.44772 4 3 4H4V3C4 2.44772 4.44772 2 5 2ZM5 12C5.55228 12 6 12.4477 6 13V14H7C7.55228 14 8 14.4477 8 15C8 15.5523 7.55228 16 7 16H6V17C6 17.5523 5.55228 18 5 18C4.44772 18 4 17.5523 4 17V16H3C2.44772 16 2 15.5523 2 15C2 14.4477 2.44772 14 3 14H4V13C4 12.4477 4.44772 12 5 12Z" fill="#FBBF24" />
                                        <path fillRule="evenodd" clipRule="evenodd" d="M11.9999 2C12.4537 2 12.8505 2.30548 12.9667 2.74411L14.1459 7.19893L17.4997 9.13381C17.8092 9.31241 17.9999 9.64262 17.9999 10C17.9999 10.3574 17.8092 10.6876 17.4997 10.8662L14.1459 12.8011L12.9667 17.2559C12.8505 17.6945 12.4537 18 11.9999 18C11.5462 18 11.1493 17.6945 11.0332 17.2559L9.85402 12.8011L6.50027 10.8662C6.19072 10.6876 6 10.3574 6 10C6 9.64262 6.19072 9.31241 6.50027 9.13382L9.85402 7.19893L11.0332 2.74411C11.1493 2.30548 11.5462 2 11.9999 2Z" fill="#FBBF24" />
                                    </svg>
                                </div>
                                <div className={s.nodeRight}>
                                    <div className={s.text}>
                                        To reply to feedback in Reputation Management, you must be listed on at least one channel. Visit the Channel Store to select and add your preferred channels.
                                    </div>
                                    <div
                                        onClick={() => {
                                            navigate('/dashboard/marketing?mainTab=external&subTab=channel');
                                        }}
                                        className={s.upText}
                                    >
                                        Browse channel store
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
                <Spin className={s.feedbackContent} spinning={loading}>
                    {
                        currentFeedbackType === EFeedbackType.NORMAL &&
                        <>
                            <div className={s.feedbackFilter}>
                                <Dropdown menu={{ items: feedbackFilterItems }} trigger={['click']}>
                                    <a onClick={(e) => e.preventDefault()}>
                                        <Space>
                                            <span className={s.filterLabel}>{feedbackFilterLabel}</span>
                                            <DownOutlined />
                                        </Space>
                                    </a>
                                </Dropdown>
                            </div>
                            <div className={s.feedbackContentIn}>
                                {
                                    feedbackData?.map((feedback: PatientsFeedback) => {
                                        const hasEmail = !!(feedback.email);
                                        const replyButtonEnabled = hasEmail;
                                        return (
                                            <div className={s.feedbackItem} key={`${feedback.id}`}>
                                                <div className={s.left}>
                                                    <div className={s.info}>
                                                        <div className={s.nameAndScore}>
                                                            <div className={s.name}>{getFullName(feedback.firstName, feedback.lastName)} {feedback.source && <span className={s.source}>{`| ${feedback.source}`}</span>}</div>
                                                        </div>
                                                        {
                                                            (feedback.email || feedback.phoneNumber) && (
                                                                <div className={s.emailAndPhone}>
                                                                    {
                                                                        feedback.email && (
                                                                            <div className={s.email}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                    <path d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z" stroke="#9CA3AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                                </svg>
                                                                                {feedback.email}
                                                                            </div>
                                                                        )
                                                                    }
                                                                    {
                                                                        feedback.phoneNumber && (
                                                                            <div className={s.phone}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                    <path d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM2 4.00065V3.33398C1.82318 3.33398 1.65361 3.40423 1.52858 3.52926C1.40355 3.65429 1.33332 3.82387 1.33333 4.00069L2 4.00065ZM2.00052 4.00065V4.66732C2.25679 4.66732 2.49038 4.52042 2.60139 4.28944C2.7124 4.05847 2.68119 3.7843 2.5211 3.58419L2.00052 4.00065ZM2 4H1.33333C1.33333 4.15138 1.38485 4.29825 1.47942 4.41646L2 4ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4.00069C1.33371 9.89141 6.10919 14.6667 12 14.6667V13.3333C6.84554 13.3333 2.66699 9.15499 2.66667 4.00061L1.33333 4.00069ZM2.00052 3.33398H2V4.66732H2.00052V3.33398ZM1.47942 4.41646L1.47994 4.41711L2.5211 3.58419L2.52058 3.58354L1.47942 4.41646ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z" fill="#9CA3AF" />
                                                                                </svg>
                                                                                {feedback.phoneNumber}
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            )
                                                        }
                                                        <div className={s.contentAndTime}>
                                                            <div className={s.content}>{feedback.feedbackContent}</div>
                                                        </div>
                                                    </div>
                                                    <div className={s.starNDate}>
                                                        <div className={s.score}><Star yellow width={80} gap={0.2} value={feedback.overallScore} /></div>
                                                        <div className={s.time}>{getTimeStr(dayjs(feedback?.answerAt), 'MMMM D, YYYY')}</div>
                                                    </div>
                                                </div>
                                                <div className={s.right}>
                                                    <Button
                                                        onClick={() => {
                                                            if (!canReply) {
                                                                setReplyUpgradeTipModalOpen(true);
                                                                return;
                                                            }
                                                            setCurrentFeedback(feedback);
                                                            setShowReply(true);
                                                        }}
                                                        className={`${replyButtonEnabled ? s.btn : s.btnDisabled}`}
                                                        disabled={!replyButtonEnabled}
                                                    >Reply
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <div className={s.paginationBox}>
                                <Pagination current={feedbackListPage} total={totalFeedback} pageSize={feedbackListPageSize} onChange={handlePaginationChange} />
                            </div>

                        </>
                    }

                    {
                        currentFeedbackType === EFeedbackType.ANONYMOUS &&
                        <>
                            <div className={s.feedbackFilter}>
                                <Dropdown menu={{ items: anonymousFeedbackFilterItems }} trigger={['click']}>
                                    <a onClick={(e) => e.preventDefault()}>
                                        <Space>
                                            <span className={s.filterLabel}>{anonymousFeedbackFilterLabel}</span>
                                            <DownOutlined />
                                        </Space>
                                    </a>
                                </Dropdown>
                            </div>
                            <div className={s.feedbackContentIn}>
                                {
                                    anonymousFeedbackData?.map((feedback: PatientsFeedback) => {
                                        return (
                                            <div className={s.feedbackItem} key={feedback.id}>
                                                <div className={s.left}>
                                                    <div className={s.nameAndScore}>
                                                        <div className={s.name}>{getTimeStr(dayjs(feedback?.answerAt), 'MMMM D, YYYY')}</div>
                                                        <div className={s.score}><Star yellow width={80} gap={0.2} value={feedback.overallScore} /></div>
                                                    </div>
                                                    <div className={s.contentAndTime}>
                                                        <div className={s.content} style={{ marginRight: '120px' }}>{feedback.feedbackContent}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <div className={s.paginationBox}>
                                <Pagination current={anonymousFeedbackListPage} total={anonymousTotalFeedback} pageSize={anonymousFeedbackListPageSize} onChange={handlePaginationChange} />
                            </div>
                        </>
                    }

                    {
                        currentFeedbackType === EFeedbackType.REPLYED &&
                        <>
                            <div className={s.feedbackFilter}>
                                <Dropdown menu={{ items: repliedFeedbackFilterItems }} trigger={['click']}>
                                    <a onClick={(e) => e.preventDefault()}>
                                        <Space>
                                            <span className={s.filterLabel}>{repliedFeedbackFilterLabel}</span>
                                            <DownOutlined />
                                        </Space>
                                    </a>
                                </Dropdown>
                            </div>
                            <div className={s.feedbackContentIn}>
                                {
                                    repliedFeedbackData?.map((feedback: PatientsFeedback) => {
                                        return (
                                            <div className={s.feedbackItem} key={feedback.id}>
                                                <div className={s.left}>
                                                    <div className={s.info}>
                                                        <div className={s.nameAndScore}>
                                                            <div className={s.name}>{`${feedback.firstName} ${feedback.lastName}`} </div>
                                                        </div>
                                                        <div className={s.contentAndTime}>
                                                            <div className={s.content}>{feedback.feedbackContent}</div>
                                                        </div>
                                                    </div>
                                                    <div className={s.starNDate}>
                                                        <div className={s.score}><Star yellow width={80} gap={0.2} value={feedback.overallScore} /></div>
                                                        <div className={s.time}>{getTimeStr(dayjs(feedback?.answerAt), 'MMMM D, YYYY')}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <div className={s.paginationBox}>
                                <Pagination current={repliedFeedbackListPage} total={repliedTotalFeedback} pageSize={repliedFeedbackListPageSize} onChange={handlePaginationChange} />
                            </div>
                        </>
                    }

                </Spin>
            </div>
            <ReplyFeedbackModal
                show={showReply}
                item={currentFeedback}
                onSuccess={() => {
                    setShowReply(false);
                }}
                onCannel={() => {
                    setShowReply(false);
                }}
            />
        </div>
    );
};

export default Feedback;
